/**
 * Recurring advanced-cron schedule: build, parse, and describe using local wall-clock
 * time-frame values. EventBridge stores UTC; the UI edits and previews in local hours.
 */
import type { FrequencyUnit, RecurringSchedule } from '@/utils/cron/types';

import {
  convertUtcToLocalHours,
  formatCronString,
  mergeCrossingMidnightHourField,
} from './cronUtils';
import {
  buildCronFieldsFromForm,
  enumerateEventBridgeFires,
  formatLocalTimeFrameRangeText,
  formatTimeFrameEndWallClock,
  formatTimeFrameStartWallClock,
  inferTimeFrameEndHour,
} from './recurringCronValidation';
import { convertUTCToLocalTime, getBrowserTimezone } from './timezoneData';

/** Recurring-tab form state (local wall clock). */
export interface RecurringCronFormState {
  frequencyUnit: FrequencyUnit;
  frequency: number;
  startingMinute: number;
  useTimeFrame: boolean;
  hoursFrom: number;
  hoursTo: number;
  dayOfWeek: string;
  dayOfMonth: string;
  month: string;
}

export interface RecurringCronFieldSelectors {
  dayOfWeek: string;
  dayOfMonth: string;
  month: string;
}

/** Builder dropdown step strings (e.g. star-slash-N) plus local wall-clock fields. */
export interface RecurringUiFieldSelectors {
  frequencyUnit: FrequencyUnit;
  recurringHours: string;
  recurringMinutes: string;
  startingMinute: number;
  useTimeFrame: boolean;
  hoursFrom: number;
  hoursTo: number;
  dayOfWeek: string;
  dayOfMonth: string;
  month: string;
}

export function recurringUiFieldsToFormState(
  fields: RecurringUiFieldSelectors
): RecurringCronFormState {
  const frequency =
    fields.frequencyUnit === 'hours'
      ? parseStepFrequency(fields.recurringHours, 1)
      : parseStepFrequency(fields.recurringMinutes, 5);

  return {
    frequencyUnit: fields.frequencyUnit,
    frequency,
    startingMinute: fields.startingMinute,
    useTimeFrame: fields.useTimeFrame,
    hoursFrom: fields.hoursFrom,
    hoursTo: fields.hoursTo,
    dayOfWeek: fields.dayOfWeek,
    dayOfMonth: fields.dayOfMonth,
    month: fields.month,
  };
}

const DEFAULT_RECURRING_FORM: RecurringCronFormState = {
  frequencyUnit: 'hours',
  frequency: 1,
  startingMinute: 0,
  useTimeFrame: false,
  hoursFrom: 9,
  hoursTo: 17,
  dayOfWeek: '1-5',
  dayOfMonth: '*',
  month: '*',
};

function parseStepFrequency(field: string, fallback: number): number {
  const stepMatch = field.match(/^\*\/(\d+)$/);
  if (stepMatch) return parseInt(stepMatch[1], 10);
  const num = parseInt(field, 10);
  return isNaN(num) ? fallback : num;
}

const SINGLE_HOUR_PATTERN = /^\d+$/;

function inferLocalStartingMinuteFromUtcFires(
  minuteField: string,
  hourField: string,
  viewerTimeZone: string,
  parsedUtcMinute: number
): number {
  const slots = enumerateEventBridgeFires(minuteField, hourField);
  if (slots.length === 0) {
    return !isNaN(parsedUtcMinute) ? parsedUtcMinute : 0;
  }

  const localMinutes = slots.map(
    (slot) => convertUTCToLocalTime(slot.hour, slot.minute, viewerTimeZone).minute
  );
  const uniqueLocalMinutes = [...new Set(localMinutes)];
  if (uniqueLocalMinutes.length === 1) {
    return uniqueLocalMinutes[0];
  }

  return !isNaN(parsedUtcMinute) ? parsedUtcMinute : 0;
}

function inferUniformHourStep(localHours: number[]): number | null {
  if (localHours.length < 2) return null;
  const gaps: number[] = [];
  for (let i = 1; i < localHours.length; i++) {
    gaps.push(localHours[i] - localHours[i - 1]);
  }
  const minGap = Math.min(...gaps);
  if (gaps.every((g) => g === minGap)) {
    return minGap;
  }
  return null;
}

/**
 * When sorted local hours have uneven gaps (common when viewing a UTC comma-hour
 * cron in another timezone), infer step from the full daily cycle including wrap.
 */
function inferCircularHourStep(localHours: number[], minute: number): number | null {
  const uniform = inferUniformHourStep(localHours);
  if (uniform !== null) return uniform;
  if (localHours.length < 2) return null;

  const sorted = [...localHours].sort((a, b) => a - b);
  const times = sorted.map((h) => h * 60 + minute);
  const gapsHours: number[] = [];
  for (let i = 1; i < times.length; i++) {
    gapsHours.push((times[i] - times[i - 1]) / 60);
  }
  gapsHours.push((24 * 60 - times[times.length - 1] + times[0]) / 60);

  const counts = new Map<number, number>();
  for (const gap of gapsHours) {
    counts.set(gap, (counts.get(gap) ?? 0) + 1);
  }

  let bestGap = 0;
  let bestCount = 0;
  for (const [gap, count] of counts) {
    if (count > bestCount || (count === bestCount && gap > bestGap)) {
      bestGap = gap;
      bestCount = count;
    }
  }

  return bestCount >= 2 ? bestGap : null;
}

/**
 * Comma-hour UTC lists encode a contiguous daily window that may cross midnight.
 * The largest gap between consecutive hours (including wrap) is outside the window.
 */
function inferCircularUtcHourWindowEndpoints(
  utcHours: number[]
): { start: number; end: number } | null {
  const sorted = [...new Set(utcHours)].sort((a, b) => a - b);
  if (sorted.length === 0) return null;
  if (sorted.length === 1) return { start: sorted[0], end: sorted[0] };

  let maxGap = -1;
  let windowStartIdx = 0;

  for (let i = 0; i < sorted.length; i++) {
    const curr = sorted[i];
    const next = sorted[(i + 1) % sorted.length];
    const gap = i === sorted.length - 1 ? 24 - curr - 1 + next : next - curr - 1;
    if (gap > maxGap) {
      maxGap = gap;
      windowStartIdx = (i + 1) % sorted.length;
    }
  }

  const endIdx = (windowStartIdx - 1 + sorted.length) % sorted.length;
  return { start: sorted[windowStartIdx], end: sorted[endIdx] };
}

/** True when comma-hour list has no holes (every UTC hour in the window is listed). */
function isContiguousUtcHourCommaList(utcHours: number[]): boolean {
  const sorted = [...new Set(utcHours)].sort((a, b) => a - b);
  if (sorted.length <= 1) return true;

  for (let i = 0; i < sorted.length; i++) {
    const curr = sorted[i];
    const next = sorted[(i + 1) % sorted.length];
    const gap = i === sorted.length - 1 ? 24 - curr - 1 + next : next - curr - 1;
    if (gap > 0) return false;
  }

  return true;
}

/** Min/max UTC wall-clock hour from a stored EventBridge hour field (no timezone shift). */
function utcHourFieldToWallClockRange(utcHourField: string): { from: number; to: number } | null {
  const isCommaHourList =
    utcHourField.includes(',') &&
    !utcHourField.includes('/') &&
    utcHourField.split(',').every((part) => SINGLE_HOUR_PATTERN.test(part.trim()));

  if (isCommaHourList) {
    const hours = utcHourField
      .split(',')
      .map((part) => parseInt(part.trim(), 10))
      .filter((hour) => !isNaN(hour));
    if (hours.length === 0) return null;
    return { from: Math.min(...hours), to: Math.max(...hours) };
  }

  const merged = mergeCrossingMidnightHourField(utcHourField);

  let minFrom = Infinity;
  let maxTo = -Infinity;
  for (const segment of merged.split(',')) {
    const rangePart = segment.trim().split('/')[0];
    const dashParts = rangePart.split('-');
    if (dashParts.length === 2) {
      const from = parseInt(dashParts[0], 10);
      const to = parseInt(dashParts[1], 10);
      if (!isNaN(from) && !isNaN(to)) {
        minFrom = Math.min(minFrom, from);
        maxTo = Math.max(maxTo, to);
      }
    } else if (SINGLE_HOUR_PATTERN.test(rangePart)) {
      const hour = parseInt(rangePart, 10);
      minFrom = Math.min(minFrom, hour);
      maxTo = Math.max(maxTo, hour);
    }
  }

  if (minFrom === Infinity || maxTo === -Infinity) return null;
  return { from: minFrom, to: maxTo };
}

function utcHourFieldToLocalWallClockRange(
  utcHourField: string,
  utcMinute: number,
  viewerTimeZone: string = getBrowserTimezone()
): { from: number; to: number } | null {
  const isCommaHourList =
    utcHourField.includes(',') &&
    !utcHourField.includes('/') &&
    utcHourField.split(',').every((part) => SINGLE_HOUR_PATTERN.test(part.trim()));

  if (isCommaHourList) {
    const hours = utcHourField
      .split(',')
      .map((part) => parseInt(part.trim(), 10))
      .filter((hour) => !isNaN(hour));
    if (hours.length === 0) return null;

    if (isContiguousUtcHourCommaList(hours)) {
      const window = inferCircularUtcHourWindowEndpoints(hours);
      if (!window) return null;

      const startLocal = convertUTCToLocalTime(window.start, utcMinute, viewerTimeZone);
      const endLocal = convertUTCToLocalTime(window.end, utcMinute, viewerTimeZone);
      return { from: startLocal.hour, to: endLocal.hour };
    }

    const slots = enumerateEventBridgeFires(String(utcMinute), utcHourField);
    if (slots.length === 0) return null;
    const localHours = slots.map((slot) => {
      const local = convertUTCToLocalTime(slot.hour, slot.minute, viewerTimeZone);
      return local.hour;
    });
    return {
      from: Math.min(...localHours),
      to: Math.max(...localHours),
    };
  }

  const merged = mergeCrossingMidnightHourField(utcHourField);
  const localHourExpr = convertUtcToLocalHours(merged, viewerTimeZone);

  let minFrom = Infinity;
  let maxTo = -Infinity;
  for (const segment of localHourExpr.split(',')) {
    const rangePart = segment.trim().split('/')[0];
    const dashParts = rangePart.split('-');
    if (dashParts.length === 2) {
      const from = parseInt(dashParts[0], 10);
      const to = parseInt(dashParts[1], 10);
      if (!isNaN(from) && !isNaN(to)) {
        minFrom = Math.min(minFrom, from);
        maxTo = Math.max(maxTo, to);
      }
    } else if (SINGLE_HOUR_PATTERN.test(rangePart)) {
      const hour = parseInt(rangePart, 10);
      minFrom = Math.min(minFrom, hour);
      maxTo = Math.max(maxTo, hour);
    }
  }

  if (minFrom === Infinity || maxTo === -Infinity) return null;
  return { from: minFrom, to: maxTo };
}

/** Map recurring form state → shared {@link RecurringSchedule} config. */
export function recurringFormToScheduleConfig(form: RecurringCronFormState): RecurringSchedule {
  const config: RecurringSchedule = {
    type: 'recurring',
    frequency: form.frequency,
    unit: form.frequencyUnit,
  };

  if (form.useTimeFrame) {
    config.timeFrame = {
      startHour: form.hoursFrom,
      endHour: form.hoursTo,
      startMinute: form.startingMinute,
    };
  }

  if (form.dayOfWeek !== '*' && form.dayOfWeek !== '?') {
    config.daysOfWeek = form.dayOfWeek.split(',');
  }

  return config;
}

/** Human-readable preview from local wall-clock config (matches form dropdowns). */
export function describeLocalRecurringSchedule(config: RecurringSchedule): string {
  const { frequency, unit, timeFrame } = config;

  if (unit === 'minutes') {
    let desc = `Every ${frequency} minute${frequency === 1 ? '' : 's'}`;
    if (timeFrame) {
      desc += `, ${formatLocalTimeFrameRangeText(timeFrame.startHour, timeFrame.endHour, {
        frequencyUnit: 'minutes',
      })}`;
    }
    return desc;
  }

  const startMin = timeFrame?.startMinute ?? 0;
  let desc = `At ${String(startMin).padStart(2, '0')} minutes past the hour, every ${frequency} hour${frequency === 1 ? '' : 's'}`;
  if (timeFrame) {
    desc += `, ${formatLocalTimeFrameRangeText(timeFrame.startHour, timeFrame.endHour, {
      startingMinute: startMin,
      frequencyUnit: 'hours',
    })}`;
  }
  return desc;
}

/** Build EventBridge cron (UTC) from local wall-clock recurring config. */
export function buildLocalRecurringCronExpression(
  form: RecurringCronFormState,
  selectors: RecurringCronFieldSelectors,
  viewerTimeZone: string = getBrowserTimezone()
): string {
  const dow = selectors.dayOfWeek;
  const dom = selectors.dayOfMonth;
  const mon = selectors.month;

  const { minute, hour } = buildCronFieldsFromForm(form, viewerTimeZone);
  return `${minute} ${hour} ${dom} ${mon} ${dow} *`;
}

/** Parse UTC EventBridge cron → local wall-clock recurring form state. */
export function parseRecurringCronFormState(
  expression: string,
  viewerTimeZone: string = getBrowserTimezone()
): RecurringCronFormState | null {
  if (!expression?.trim()) return null;

  const parts = expression.trim().split(/\s+/);
  if (parts.length < 5) return null;

  const [minuteField, hourField, domField, monthField, dowField] = parts;
  const hasMinuteStep = minuteField.includes('/');
  const hasHourStep = hourField.includes('/');
  // An hour range without a step (e.g. `3-11`) is "every hour within a time
  // frame" (frequency 1) — still a recurring schedule, not a fixed time.
  const hasHourRange = hourField.includes('-');
  const isCommaHourList =
    hourField.includes(',') &&
    !hourField.includes('/') &&
    hourField.split(',').every((part) => SINGLE_HOUR_PATTERN.test(part.trim()));
  if (!hasMinuteStep && !hasHourStep && !hasHourRange && !isCommaHourList) return null;

  const form: RecurringCronFormState = { ...DEFAULT_RECURRING_FORM };

  const utcMinute = parseInt(minuteField, 10);
  const parsedUtcMinute = isNaN(utcMinute) ? 0 : utcMinute;

  if (hasMinuteStep) {
    form.frequencyUnit = 'minutes';
    form.frequency = parseStepFrequency(minuteField, 5);
    if (hourField.includes('-') || hourField.includes(',')) {
      form.useTimeFrame = true;
      const range = utcHourFieldToLocalWallClockRange(hourField, parsedUtcMinute, viewerTimeZone);
      if (range) {
        form.hoursFrom = range.from;
        form.hoursTo = range.to;
      }
    }
  } else if (isCommaHourList) {
    form.frequencyUnit = 'hours';
    form.useTimeFrame = true;

    const slots = enumerateEventBridgeFires(minuteField, hourField);

    form.startingMinute = inferLocalStartingMinuteFromUtcFires(
      minuteField,
      hourField,
      viewerTimeZone,
      parsedUtcMinute
    );

    const localHours = slots
      .map((slot) => convertUTCToLocalTime(slot.hour, slot.minute, viewerTimeZone).hour)
      .sort((a, b) => a - b);

    const utcHours = hourField
      .split(',')
      .map((part) => parseInt(part.trim(), 10))
      .filter((hour) => !isNaN(hour));
    const contiguousHours = isContiguousUtcHourCommaList(utcHours);

    const range = utcHourFieldToLocalWallClockRange(hourField, form.startingMinute, viewerTimeZone);
    if (range) {
      form.hoursFrom = range.from;
    }

    if (localHours.length > 0) {
      if (!contiguousHours) {
        form.hoursFrom = localHours[0];
      }
      const step = inferCircularHourStep(localHours, form.startingMinute);
      if (step !== null) {
        form.frequency = step;
        form.hoursTo = inferTimeFrameEndHour(form.hoursFrom, localHours, step, viewerTimeZone);
      } else if (!contiguousHours) {
        form.hoursTo = localHours[localHours.length - 1];
      }
    }
  } else {
    form.frequencyUnit = 'hours';

    if (hourField.includes('-') && hasHourStep) {
      const normalized = mergeCrossingMidnightHourField(hourField);
      form.frequency = parseStepFrequency(normalized.split('/')[1] ?? hourField, 1);
      form.useTimeFrame = true;
      const isLegacyMidnightSplit = hourField.includes(',') && hourField.includes('/');

      if (isLegacyMidnightSplit) {
        if (!isNaN(parsedUtcMinute)) form.startingMinute = parsedUtcMinute;
        const range = utcHourFieldToLocalWallClockRange(
          normalized,
          form.startingMinute,
          viewerTimeZone
        );
        if (range) {
          form.hoursFrom = range.from;
          form.hoursTo = range.to;
        }
      } else {
        form.startingMinute = inferLocalStartingMinuteFromUtcFires(
          minuteField,
          hourField,
          viewerTimeZone,
          parsedUtcMinute
        );

        const rangeMatch = normalized.match(/^(\d+)-(\d+)/);
        if (rangeMatch) {
          const utcStartHour = parseInt(rangeMatch[1], 10);
          const utcEndHour = parseInt(rangeMatch[2], 10);
          if (!isNaN(utcStartHour) && !isNaN(utcEndHour)) {
            const startLocal = convertUTCToLocalTime(utcStartHour, parsedUtcMinute, viewerTimeZone);
            const endLocal = convertUTCToLocalTime(utcEndHour, parsedUtcMinute, viewerTimeZone);
            form.hoursFrom = startLocal.hour;
            form.hoursTo = endLocal.hour;
          }
        }
      }
    } else if (hasHourStep) {
      if (!isNaN(parsedUtcMinute)) form.startingMinute = parsedUtcMinute;
      form.frequency = parseStepFrequency(hourField, 1);
    } else if (hourField.includes('-')) {
      form.startingMinute = inferLocalStartingMinuteFromUtcFires(
        minuteField,
        hourField,
        viewerTimeZone,
        parsedUtcMinute
      );
      // Range without an explicit step → every hour (frequency 1) within a time frame.
      form.frequency = 1;
      form.useTimeFrame = true;
      const range = utcHourFieldToLocalWallClockRange(
        hourField,
        form.startingMinute,
        viewerTimeZone
      );
      if (range) {
        form.hoursFrom = range.from;
        form.hoursTo = range.to;
      }
    }
  }

  form.dayOfWeek = dowField || '*';
  form.dayOfMonth = domField || '*';
  form.month = monthField || '*';

  return form;
}

/**
 * Human-readable preview from the stored UTC cron (matches UTC hour-field boundaries).
 * Avoids cronstrue ":59" end-of-hour wording for time-frame ranges.
 */
export function describeUtcRecurringSchedule(expression: string): string | null {
  if (!expression?.trim()) return null;

  const parts = expression.trim().split(/\s+/);
  if (parts.length < 5) return null;

  const [minuteField, hourField] = parts;
  const hasMinuteStep = minuteField.includes('/');
  const hasHourStep = hourField.includes('/');
  const hasHourRange = hourField.includes('-');
  const isCommaHourList =
    hourField.includes(',') &&
    !hourField.includes('/') &&
    hourField.split(',').every((part) => SINGLE_HOUR_PATTERN.test(part.trim()));

  if (!hasMinuteStep && !hasHourStep && !hasHourRange && !isCommaHourList) {
    return null;
  }

  // Hour-step without a range in the hour field (e.g. `0 0/5` with dom `9-17`) — not a UTC time frame.
  if (hasHourStep && !hasHourRange && !isCommaHourList) {
    return null;
  }

  const utcMinute = parseInt(minuteField, 10);
  const parsedUtcMinute = isNaN(utcMinute) ? 0 : utcMinute;
  const hasTimeFrame = hasHourRange || isCommaHourList;
  const range = hasTimeFrame ? utcHourFieldToWallClockRange(hourField) : null;

  if (hasMinuteStep) {
    const frequency = parseStepFrequency(minuteField, 5);
    let desc = `Every ${frequency} minute${frequency === 1 ? '' : 's'}`;
    if (range) {
      desc += `, between ${formatTimeFrameStartWallClock(range.from, 0)} and ${formatTimeFrameEndWallClock(range.to)}`;
    }
    return desc;
  }

  if (isCommaHourList) {
    const slots = enumerateEventBridgeFires(minuteField, hourField);
    const utcHours = slots.map((slot) => slot.hour).sort((a, b) => a - b);
    const step = inferUniformHourStep(utcHours);
    const startMin = /^\d+$/.test(minuteField) ? parseInt(minuteField, 10) : parsedUtcMinute;
    let desc = `At ${String(startMin).padStart(2, '0')} minutes past the hour`;
    if (step !== null) {
      desc += `, every ${step} hour${step === 1 ? '' : 's'}`;
    }
    if (utcHours.length > 0) {
      const endHour =
        step !== null
          ? inferTimeFrameEndHour(utcHours[0], utcHours, step, 'UTC')
          : utcHours[utcHours.length - 1];
      desc += `, between ${formatTimeFrameStartWallClock(utcHours[0], startMin)} and ${formatTimeFrameEndWallClock(endHour)}`;
    }
    return desc;
  }

  const frequency = hasHourStep
    ? parseStepFrequency(mergeCrossingMidnightHourField(hourField).split('/')[1] ?? hourField, 1)
    : hasHourRange
      ? 1
      : parseStepFrequency(hourField, 1);

  let desc = `At ${String(parsedUtcMinute).padStart(2, '0')} minutes past the hour, every ${frequency} hour${frequency === 1 ? '' : 's'}`;
  if (range) {
    desc += `, between ${formatTimeFrameStartWallClock(range.from, parsedUtcMinute)} and ${formatTimeFrameEndWallClock(range.to)}`;
  }
  return desc;
}

/** UTC cronstrue description for stored cronExplain / tooltip. */
export function describeUtcCronExpression(expression: string): string {
  if (!expression.trim()) return '';
  return formatCronString(expression);
}
